import React, { useEffect, useRef } from 'react';

/**
 * PerfGraphOverlayContent — kit tool `perfGraph`. Live FPS sparkline drawn on a
 * canvas from per-frame `performance.now()` deltas (60 FPS = full height, the
 * mid-line ≈ 30 FPS). Recreated into the 🛠 kit during the dev-tools
 * modernization arc (2026-05-29); the rAF runs while mounted and stops on
 * unmount (no isPaused/isActive props).
 */
export const PerfGraphOverlayContent: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const perfHistoryRef = useRef<number[]>([]);

  useEffect(() => {
    let animationFrameId: number;
    let lastTime = performance.now();

    const renderGraph = () => {
      const now = performance.now();
      const delta = now - lastTime;
      lastTime = now;
      const fps = 1000 / delta;

      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          perfHistoryRef.current.push(fps);
          if (perfHistoryRef.current.length > canvas.width) perfHistoryRef.current.shift();

          ctx.fillStyle = '#0a0f1e';
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          ctx.strokeStyle = '#1a2639';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(0, canvas.height / 2); // ≈ 30 FPS line
          ctx.lineTo(canvas.width, canvas.height / 2);
          ctx.stroke();

          ctx.strokeStyle = '#00ff00';
          ctx.lineWidth = 2;
          ctx.beginPath();
          perfHistoryRef.current.forEach((value, index) => {
            const y = canvas.height - (value / 60) * canvas.height; // 60 FPS max
            if (index === 0) ctx.moveTo(index, y);
            else ctx.lineTo(index, y);
          });
          ctx.stroke();

          ctx.fillStyle = '#ffffff';
          ctx.font = '14px monospace';
          ctx.fillText(`FPS: ${fps.toFixed(1)}`, 10, 20);
        }
      }

      animationFrameId = requestAnimationFrame(renderGraph);
    };

    renderGraph();
    return () => { if (animationFrameId) cancelAnimationFrame(animationFrameId); };
  }, []);

  return (
    <div style={{ padding: 10 }}>
      <h3 style={{ color: '#64c8ff', margin: '0 0 8px' }}>Performance Graph</h3>
      <canvas
        ref={canvasRef}
        width={620}
        height={150}
        style={{ border: '1px solid #333', background: '#000', width: '100%', height: 150 }}
      />
      <div style={{ marginTop: 10 }}>
        <button
          onClick={() => { perfHistoryRef.current = []; }}
          style={{ background: '#3498db', border: 'none', color: 'white', padding: '6px 12px', borderRadius: 4, cursor: 'pointer' }}
        >
          🗑️ Clear Graph
        </button>
      </div>
    </div>
  );
};
